import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, X, Wrench } from 'lucide-react';
import ModelThumb from './ModelThumb';
import { useT } from '../services/i18n';
import { getRecentVehicles, clearRecentVehicles, RECENT_VEHICLES_EVENT } from '../services/recentVehiclesStore';

const toPath = (url) => { try { return new URL(url, window.location.origin).pathname; } catch { return '/vehicules'; } };
// "RENAULT Clio V" / "ALFA ROMEO Giulia" → [brand, model] (brand words are stored upper-case)
const splitAlt = (alt) => {
  const words = (alt || '').split(' ');
  let i = 1;
  while (i < words.length - 1 && /^[A-ZÀ-Ý-]{3,}$/.test(words[i])) i += 1;
  return [words.slice(0, i).join(' '), words.slice(i).join(' ')];
};

// Home: the last vehicle models the visitor looked at — photo cards (same look as the popular models)
const RecentVehicles = () => {
  const { t } = useT();
  const [items, setItems] = useState(getRecentVehicles);
  useEffect(() => {
    const refresh = () => setItems(getRecentVehicles());
    window.addEventListener(RECENT_VEHICLES_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => { window.removeEventListener(RECENT_VEHICLES_EVENT, refresh); window.removeEventListener('storage', refresh); };
  }, []);
  if (!items.length) return null;
  const gallery = items.filter((v) => v.src).map((v) => ({ src: v.src, alt: v.alt, url: v.url }));
  return (
    <section className="bg-white border-b border-gray-100" data-testid="recent-vehicles">
      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2"><Car size={18} className="text-orange-500" /> {t('home.recentVehicles')} <span className="text-sm font-normal text-gray-500">({items.length})</span></h2>
          <button type="button" data-testid="recent-vehicles-clear" onClick={clearRecentVehicles} className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1 transition-colors"><X size={12} /> {t('home.recentClear')}</button>
        </div>
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3" data-testid="recent-vehicles-list">
          {items.map((v, i) => {
            const [brand, model] = splitAlt(v.alt);
            return (
              <li key={v.src} className="group bg-white rounded-xl border border-gray-200 hover:border-orange-400 hover:shadow-md transition-all flex flex-col" data-testid={`recent-vehicle-${v.alt}`}>
                <div className="w-full aspect-square max-h-[120px] flex items-center justify-center p-3 pb-1">
                  {v.src ? <ModelThumb src={v.src} alt={v.alt} size={100} testId={`recent-vehicle-image-${i}`} className="group-hover:scale-105" gallery={gallery} shareUrl={v.url} /> : <Car size={36} className="text-gray-200" />}
                </div>
                <Link to={toPath(v.url)} data-testid={`recent-vehicle-link-${i}`} className="flex-1 flex flex-col items-center text-center px-3 pb-3">
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">{brand}</span>
                  <span className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2 group-hover:text-orange-700">{model || brand}</span>
                  <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-white bg-[#1e3a5f] group-hover:bg-orange-500 px-2 py-1 rounded-full transition-colors"><Wrench size={10} /> {t('home.topModelsParts')}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default RecentVehicles;
